import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import RequireDealer from "@/components/RequireDealer";
import { useCart } from "@/context/CartContext";
import { PRICE_STAND } from "@/data/priceList";
import { formatEuro, getDealerSession } from "@/utils/dealerSession";
import { useLang } from "@/i18n";

const FORM_KEY = "mueller_order_form_v1";

function Cart() {
    const { t } = useLang();
    const cart = useCart();
    const session = getDealerSession();
    const [form, setForm] = useState(() => {
        let saved = {};
        try {
            const raw = localStorage.getItem(FORM_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (parsed && typeof parsed === "object") saved = parsed;
        } catch {
            /* ignore */
        }
        return {
            firma: saved.firma || "",
            name: saved.name || "",
            kdnr: saved.kdnr || session?.kdn || "",
            email: saved.email || "",
            telefon: saved.telefon || "",
            bemerkung: saved.bemerkung || "",
            website: "",
        };
    });
    const [state, setState] = useState("idle"); // idle | sending | sent | mailto
    const [error, setError] = useState("");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // Angaben lokal auf dem Gerät des Händlers speichern
    useEffect(() => {
        try {
            const { firma, name, kdnr, email, telefon, bemerkung } = form;
            localStorage.setItem(FORM_KEY, JSON.stringify({ firma, name, kdnr, email, telefon, bemerkung }));
        } catch {
            /* ignore */
        }
    }, [form]);

    const clearSavedForm = () => {
        try {
            localStorage.removeItem(FORM_KEY);
        } catch {
            /* ignore */
        }
        setForm((f) => ({ ...f, firma: "", name: "", kdnr: "", email: "", telefon: "", bemerkung: "" }));
    };

    const buildBody = () => {
        const lines = cart.items.map((it, i) =>
            `${String(i + 1).padStart(2, "0")}. Art.-Nr. ${it.sku}${it.ref ? ` | Typ ${it.ref}` : ""} | ${it.name} | ${it.qty} x ${formatEuro(it.price)} = ${formatEuro(it.qty * it.price)}`
        );
        return [
            t("NEUE BESTELLUNG ÜBER DEN HÄNDLERSHOP", "NEW ORDER VIA THE DEALER SHOP"),
            "",
            `${t("Händler", "Dealer")}: ${form.firma ? form.firma + " — " : ""}${form.name}`,
            `${t("Kundennummer", "Customer no.")}: ${form.kdnr || "-"}`,
            `E-Mail: ${form.email}`,
            `${t("Telefon", "Phone")}: ${form.telefon || "-"}`,
            "",
            t("BESTELLTE ARTIKEL", "ORDERED ITEMS"),
            "----------------------------------------",
            ...lines,
            "----------------------------------------",
            `${t("SUMME (netto)", "TOTAL (net)")} (Shop): ${formatEuro(cart.totalCents)}`,
            t("Alle Preise netto zzgl. gesetzlicher MwSt. und Versandkosten, ab Lager Hemhofen.", "All prices net plus statutory VAT and shipping, ex warehouse Hemhofen."),
            `${t("Preisstand", "Price basis")}: Fachhandelskatalog ${PRICE_STAND}`,
            "",
            `${t("Bemerkung", "Note")}: ${form.bemerkung || "-"}`,
        ].join("\n");
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) || cart.items.length === 0) {
            setError(t("Bitte Name, gültige E-Mail und mindestens einen Artikel angeben.", "Please provide a name, a valid email and at least one item."));
            return;
        }
        setState("sending");
        const payload = {
            firma: form.firma,
            name: form.name,
            kdnr: form.kdnr,
            email: form.email,
            telefon: form.telefon,
            bemerkung: form.bemerkung,
            website: form.website,
            items: cart.items.map((it) => ({ sku: it.sku, name: it.name, ref: it.ref, qty: it.qty, price: it.price })),
        };
        try {
            const res = await fetch("bestellung.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.ok) {
                cart.clear();
                setState("sent");
                return;
            }
        } catch {
            /* fallback unten */
        }
        // Fallback: E-Mail-Programm öffnen
        const subject = encodeURIComponent(`${t("Bestellung Händler", "Dealer order")} ${form.kdnr || ""} ${form.name}`);
        window.location.href = `mailto:vertrieb@satmueller.de?subject=${subject}&body=${encodeURIComponent(buildBody())}`;
        setState("mailto");
    };

    if (state === "sent" || state === "mailto") {
        return (
            <main data-testid="page-cart" className="pt-28 sm:pt-32 pb-20 sm:pb-24">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-12">
                    <Reveal>
                        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 text-center" data-testid="order-success">
                            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
                            <h1 className="mt-5 font-display text-2xl sm:text-3xl font-bold text-slate-900">
                                {state === "sent" ? t("Bestellung gesendet!", "Order sent!") : t("Bestellung vorbereitet!", "Order prepared!")}
                            </h1>
                            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                {t(
                                    "Ihre Bestellung ist bei uns eingegangen. Wir bestätigen Verfügbarkeit und verbindliche Preise schnellstmöglich — bei Fragen: +49 (0) 9195 / 9404-0.",
                                    "We have received your order. We will confirm availability and binding prices as soon as possible — any questions: +49 (0) 9195 / 9404-0."
                                )}
                            </p>
                            {state === "mailto" && (
                                <p className="mt-4 text-sm font-semibold text-amber-700" data-testid="order-mailto-note">
                                    {t(
                                        "Die Bestellung wurde in Ihrem E-Mail-Programm vorbereitet — bitte dort noch auf Senden klicken.",
                                        "Your order has been prepared in your email program — please press send there."
                                    )}
                                </p>
                            )}
                            {cart.items.length > 0 ? (
                                <div className="mt-7 rounded-xl bg-slate-50 border border-slate-200 p-5" data-testid="cart-clear-question">
                                    <p className="font-semibold text-slate-900 text-sm sm:text-base">
                                        {t("Darf der Warenkorb gelöscht werden?", "May the cart be cleared?")}
                                    </p>
                                    <p className="mt-1.5 text-xs text-slate-500">
                                        {t(
                                            "Behalten Sie ihn z. B., wenn Sie dieselben Artikel bald wieder bestellen möchten.",
                                            "Keep it, for example, if you want to reorder the same items soon."
                                        )}
                                    </p>
                                    <div className="mt-4 flex flex-col sm:flex-row justify-center gap-3">
                                        <button
                                            onClick={() => cart.clear()}
                                            data-testid="cart-clear-yes"
                                            className="inline-flex items-center justify-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-6 py-3 transition-colors"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            {t("Ja, Warenkorb leeren", "Yes, clear the cart")}
                                        </button>
                                        <Link
                                            to="/haendler/shop"
                                            data-testid="cart-clear-keep"
                                            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 hover:border-red-400 text-slate-700 hover:text-red-700 text-sm font-semibold px-6 py-3 transition-colors"
                                        >
                                            {t("Nein, beibehalten", "No, keep it")}
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <p className="mt-5 text-sm font-semibold text-emerald-700" data-testid="cart-cleared-note">
                                    {t("Der Warenkorb wurde gelöscht.", "The cart has been cleared.")}
                                </p>
                            )}
                            {cart.items.length > 0 && (
                                <p className="mt-4 text-sm text-slate-500" data-testid="cart-kept-note">
                                    {t(
                                        `Warenkorb beibehalten: ${cart.count} Artikel, ${formatEuro(cart.totalCents)}.`,
                                        `Cart kept: ${cart.count} items, ${formatEuro(cart.totalCents)}.`
                                    )}
                                </p>
                            )}
                            <Link
                                to="/haendler/shop"
                                data-testid="order-success-back"
                                className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-6 py-3 transition-colors"
                            >
                                {t("Weiteren Artikel bestellen", "Order another item")}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </main>
        );
    }

    const field = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:outline-none";

    return (
        <main data-testid="page-cart" className="pt-28 sm:pt-32 pb-20 sm:pb-24">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="breadcrumb">
                    <Link to="/haendler/shop" data-testid="breadcrumb-shop" className="hover:text-red-700 transition-colors">
                        {t("Händler-Shop", "Dealer shop")}
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-slate-600">{t("Warenkorb", "Cart")}</span>
                </p>

                <Reveal>
                    <div className="mt-6">
                        <Eyebrow>{t("Händler-Shop · Warenkorb", "Dealer shop · Cart")}</Eyebrow>
                        <h1 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                            {t("Ihr Warenkorb.", "Your cart.")}
                        </h1>
                    </div>
                </Reveal>

                {cart.items.length === 0 ? (
                    <Reveal>
                        <div className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-10 text-center" data-testid="cart-empty">
                            <ShoppingCart className="h-10 w-10 text-slate-300 mx-auto" />
                            <p className="mt-4 font-semibold text-slate-900">{t("Der Warenkorb ist leer.", "Your cart is empty.")}</p>
                            <Link
                                to="/haendler/shop"
                                data-testid="cart-empty-back"
                                className="mt-5 inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-6 py-3 transition-colors"
                            >
                                {t("Zum Bestellshop", "To the shop")}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                ) : (
                    <div className="mt-8 grid lg:grid-cols-5 gap-6 items-start">
                        <div className="lg:col-span-3 space-y-3" data-testid="cart-items">
                            {cart.items.map((it) => (
                                <div key={it.sku} className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`cart-row-${it.sku}`}>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-mono text-[11px] text-red-700">{it.sku}{it.ref ? ` · Typ ${it.ref}` : ""}</p>
                                        <p className="text-sm font-semibold text-slate-900 leading-snug">{it.name}</p>
                                        <p className="text-xs text-slate-500 mt-1">{formatEuro(it.price)} / {t("Stück", "unit")}</p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={() => cart.setQty(it.sku, it.qty - 1)}
                                            data-testid={`cart-minus-${it.sku}`}
                                            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-300 text-slate-600 hover:border-red-400 hover:text-red-700 transition-colors"
                                            aria-label={t("Menge verringern", "Decrease quantity")}
                                        >
                                            <Minus className="h-3.5 w-3.5" />
                                        </button>
                                        <input
                                            type="number"
                                            min="1"
                                            max="9999"
                                            value={it.qty}
                                            onChange={(e) => cart.setQty(it.sku, parseInt(e.target.value, 10) || 0)}
                                            data-testid={`cart-qty-${it.sku}`}
                                            className="w-16 rounded-lg border border-slate-300 px-2 py-2 text-sm text-center focus:border-red-500 focus:outline-none"
                                        />
                                        <button
                                            onClick={() => cart.setQty(it.sku, it.qty + 1)}
                                            data-testid={`cart-plus-${it.sku}`}
                                            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-300 text-slate-600 hover:border-red-400 hover:text-red-700 transition-colors"
                                            aria-label={t("Menge erhöhen", "Increase quantity")}
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:w-44 shrink-0">
                                        <span className="font-semibold text-slate-900" data-testid={`cart-line-total-${it.sku}`}>{formatEuro(it.qty * it.price)}</span>
                                        <button
                                            onClick={() => cart.remove(it.sku)}
                                            data-testid={`cart-remove-${it.sku}`}
                                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-700 transition-colors"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            {t("Entfernen", "Remove")}
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <div className="flex items-center justify-between rounded-xl bg-slate-900 text-white px-5 py-4" data-testid="cart-total">
                                <span className="text-sm font-semibold">{t("Summe (netto, lt. Shop)", "Total (net, per shop)")}</span>
                                <span className="font-display text-xl font-bold">{formatEuro(cart.totalCents)}</span>
                            </div>
                            <p className="text-xs text-slate-400" data-testid="cart-netto-note">
                                {t(
                                    "Alle Preise verstehen sich netto in Euro, zuzüglich gesetzlicher Mehrwertsteuer und zzgl. Versandkosten, ab Lager Hemhofen. Preisstand: Fachhandelskatalog Januar 2026 — Irrtümer vorbehalten, verbindlich ist die Auftragsbestätigung des Vertriebs.",
                                    "All prices are net in EUR, plus statutory VAT and shipping costs, ex warehouse Hemhofen. Price basis: trade catalogue January 2026 — subject to errors; the sales team's order confirmation is binding."
                                )}
                            </p>
                        </div>

                        <Reveal delay={0.1} className="lg:col-span-2">
                            <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6" data-testid="order-form">
                                <h2 className="font-display text-lg font-bold text-slate-900">{t("Bestellung abschicken", "Send the order")}</h2>
                                <p className="mt-1.5 text-xs text-slate-500">
                                    {t("Die Bestellung geht als E-Mail an vertrieb@satmueller.de.", "The order is sent as an email to vertrieb@satmueller.de.")}
                                </p>
                                <div className="mt-5 space-y-3">
                                    <input type="text" value={form.firma} onChange={set("firma")} placeholder={t("Firma (optional)", "Company (optional)")} data-testid="order-firma-input" className={field} />
                                    <input type="text" value={form.name} onChange={set("name")} placeholder={t("Ansprechpartner *", "Contact person *")} data-testid="order-name-input" className={field} />
                                    <input type="text" value={form.kdnr} onChange={set("kdnr")} placeholder={t("Kundennummer", "Customer number")} data-testid="order-kdnr-input" className={field} />
                                    <input type="email" value={form.email} onChange={set("email")} placeholder={t("E-Mail *", "Email *")} data-testid="order-email-input" className={field} />
                                    <input type="tel" value={form.telefon} onChange={set("telefon")} placeholder={t("Telefon (optional)", "Phone (optional)")} data-testid="order-phone-input" className={field} />
                                    <textarea value={form.bemerkung} onChange={set("bemerkung")} rows="3" placeholder={t("Bemerkung / Lieferadresse (optional)", "Note / delivery address (optional)")} data-testid="order-note-input" className={field} />
                                    <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex="-1" autoComplete="off" aria-hidden="true" className="hidden" />
                                </div>
                                {error && (
                                    <p className="mt-3 text-sm font-semibold text-red-700" data-testid="order-error">{error}</p>
                                )}
                                {state === "mailto" && (
                                    <p className="mt-3 text-sm font-semibold text-amber-700" data-testid="order-mailto-note">
                                        {t(
                                            "Die Bestellung wurde in Ihrem E-Mail-Programm vorbereitet — bitte dort auf Senden klicken.",
                                            "Your order has been prepared in your email program — please press send there."
                                        )}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    disabled={state === "sending"}
                                    data-testid="order-submit-button"
                                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-red-700 hover:bg-red-800 disabled:opacity-60 text-white font-semibold text-sm px-6 py-3.5 transition-colors"
                                >
                                    {state === "sending" ? t("Wird gesendet …", "Sending …") : t("Bestellung abschicken", "Send order")}
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                                <p className="mt-3 text-[11px] text-slate-400">
                                    {t("Mit dem Absenden fordern Sie kein Abrufvermögen an — wir bestätigen die Bestellung separat.", "Submitting does not trigger a direct debit — we confirm the order separately.")}
                                </p>
                                <div className="mt-3 flex items-start justify-between gap-3" data-testid="order-save-hint">
                                    <p className="text-[11px] text-slate-400">
                                        {t(
                                            "Ihre Angaben werden auf diesem Gerät gespeichert und beim nächsten Besuch automatisch eingetragen.",
                                            "Your details are stored on this device and filled in automatically on your next visit."
                                        )}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={clearSavedForm}
                                        data-testid="order-clear-saved-button"
                                        className="shrink-0 text-[11px] font-semibold text-slate-400 underline underline-offset-2 hover:text-red-700 transition-colors"
                                    >
                                        {t("Löschen", "Delete")}
                                    </button>
                                </div>
                            </form>
                        </Reveal>
                    </div>
                )}
            </div>
        </main>
    );
}

export default function CartPage() {
    return (
        <RequireDealer>
            <Cart />
        </RequireDealer>
    );
}
